import { FC } from 'react';

import { push } from 'connected-react-router';
import { isAccountStatusReadOnly } from 'utils/clickme';
import { CAMPAIGNS_DASHBOARD_PAGE_ROUTE, CREATE_MENU_PAGE_ROUTE, MULTI_CREATION_ROUTE } from 'utils/constants/app';
import { TABS_CONTAINER_CORRECTIONS } from 'utils/constants/common';

import multicreationOpenButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_open_button_click';
import campaignsDashboardTabsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_tabs_button_click';
import campaignCreateButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_button_click';
import { Button, Flex, Tab, Tabs, useBreakpoint } from '@hh.ru/magritte-ui';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useTrls from 'hooks/translation/useTrls';

import {
    CampaignsTab,
    DEFAULT_PAGE_NUMBER,
    setCampaignsCurrentTab,
    setCheckedCampaignIds,
    setIsCampaignsDetailsLoading,
    setIsCampaignsLoading,
    setIsCampaignsStatisticsLoading,
} from 'store/models/campaignsDashboard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignsDashboard/components/CampaignsTabsPanel.module.less';

const TrlKeys = {
    tabs: {
        all: 'CampaignsDashboard.CampaignsTabs.tab.all',
        active: 'CampaignsDashboard.CampaignsTabs.tab.active',
        paused: 'CampaignsDashboard.CampaignsTabs.tab.paused',
        attention: 'CampaignsDashboard.CampaignsTabs.tab.attention',
        archived: 'CampaignsDashboard.CampaignsTabs.tab.archived',
    },
    createButton: 'CampaignsDashboard.CampaignsTabs.createButton',
    multipleCreateButton: 'CampaignsDashboard.CampaignsTabs.multipleCreateButton',
};

export const CampaignsTabsPanel: FC = () => {
    const campaignsCounts = useSelector((state) => state.campaignsDashboard.campaignsCounts);
    const campaignsCurrentTab = useSelector((state) => state.campaignsDashboard.campaignsCurrentTab);
    const currentAccountStatus = useSelector(({ user }) => user.user?.currentAccount.status);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const handleCurrentTabChange = (newTab: string) => {
        if (newTab === campaignsCurrentTab) {
            return;
        }

        dispatch([
            setIsCampaignsLoading(true),
            setIsCampaignsStatisticsLoading(true),
            setIsCampaignsDetailsLoading(true),
            setCampaignsCurrentTab(newTab as CampaignsTab),
            setCheckedCampaignIds(new Set()),
        ]);

        campaignsDashboardTabsButtonClick({
            campaignTab: newTab as CampaignsTab,
            campaignCount: campaignsCounts[newTab as CampaignsTab] ?? 0,
        });

        dispatch(push(`${CAMPAIGNS_DASHBOARD_PAGE_ROUTE}${newTab}?page=${DEFAULT_PAGE_NUMBER}`));
    };

    return (
        <Flex justify="space-between" gap={12}>
            <div className={styles.tabs}>
                <Tabs
                    activeItemId={campaignsCurrentTab}
                    mode="primary"
                    containerCorrections={TABS_CONTAINER_CORRECTIONS}
                    onChange={handleCurrentTabChange}
                >
                    {Object.values(CampaignsTab).map((tab) => (
                        <Tab
                            key={`tab-${tab}`}
                            data-qa={`campaigns-tab-${tab}`}
                            id={tab}
                            postfix={
                                [CampaignsTab.All, CampaignsTab.Archived].includes(tab)
                                    ? undefined
                                    : campaignsCounts[tab as CampaignsTab]?.toString()
                            }
                        >
                            {trls(TrlKeys.tabs[tab])}
                        </Tab>
                    ))}
                </Tabs>
            </div>

            {!isMobile && !isAccountStatusReadOnly(currentAccountStatus) && (
                <Flex columnGap={12}>
                    <Button
                        Element={SPALink}
                        to={MULTI_CREATION_ROUTE}
                        style="neutral"
                        mode="secondary"
                        size="small"
                        data-qa="campaigns-multiple-create-button"
                        onClick={() => multicreationOpenButtonClick()}
                    >
                        {trls(TrlKeys.multipleCreateButton)}
                    </Button>
                    <Button
                        Element={SPALink}
                        to={CREATE_MENU_PAGE_ROUTE}
                        style="accent"
                        mode="primary"
                        size="small"
                        data-qa="campaigns-create-button"
                        onClick={() => campaignCreateButtonClick()}
                    >
                        {trls(TrlKeys.createButton)}
                    </Button>
                </Flex>
            )}
        </Flex>
    );
};
